import { z } from 'zod';

import {
  actorSnapshotSchema,
  entityIdSchema,
  nonEmptyTextSchema,
  utcDateTimeSchema,
} from './primitives';
import { personaSchema, resourceScopeSchema } from './tenant-rbac';

export const feedbackCategorySchema = z.enum([
  'performance',
  'error',
  'compatibility',
  'usability',
  'feature-request',
  'copy-design',
  'data-error',
  'safety-improvement',
]);

export const feedbackClientContextSchema = z
  .object({
    consent: z
      .object({
        diagnostics: z.literal(true),
        screenshot: z.boolean(),
      })
      .strict(),
    appVersion: nonEmptyTextSchema.max(100),
    viewportWidth: z.number().int().positive(),
    viewportHeight: z.number().int().positive(),
    /** Deprecated diagnostic hint. G0/G1 intake strips it before persistence. */
    userAgent: nonEmptyTextSchema.max(1_000).optional(),
    browser: z.string().trim().max(100).nullable(),
    operatingSystem: z.string().trim().max(100).nullable(),
    deviceClass: z.enum(['phone', 'tablet', 'desktop', 'shared-terminal', 'unknown']),
    networkState: z.enum(['online', 'offline', 'slow', 'unknown']),
    loadDurationMs: z.number().nonnegative().nullable(),
    errorTraceId: entityIdSchema.nullable(),
    requestTraceIds: z.array(entityIdSchema).max(20),
    persona: personaSchema.nullable(),
    siteId: entityIdSchema.nullable(),
    screenshotManifestId: entityIdSchema.nullable(),
    screenshotEvidenceSha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/u)
      .nullable(),
  })
  .strict()
  .superRefine((client, context) => {
    if (
      (client.screenshotManifestId !== null || client.screenshotEvidenceSha256 !== null) &&
      !client.consent.screenshot
    ) {
      context.addIssue({
        code: 'custom',
        path: ['screenshotManifestId'],
        message: 'A screenshot requires explicit screenshot consent',
      });
    }
    if ((client.screenshotManifestId === null) !== (client.screenshotEvidenceSha256 === null))
      context.addIssue({
        code: 'custom',
        path: ['screenshotManifestId'],
        message: 'Screenshot manifest and evidence hash must be supplied together',
      });
  });

export const feedbackReportSchema = z
  .object({
    category: feedbackCategorySchema,
    description: z.string().trim().min(1).max(2_000),
    route: z.string().trim().min(1).max(2_000),
    occurredAt: utcDateTimeSchema,
    clientContext: feedbackClientContextSchema.nullable(),
  })
  .strict();

export const feedbackStatusSchema = z.enum([
  'received',
  'merged-duplicate',
  'analyzing',
  'reproducing',
  'needs-information',
  'in-development',
  'verifying',
  'preview',
  'awaiting-approval',
  'released',
  'deferred',
  'rejected',
]);

export const feedbackRecordSchema = z
  .object({
    feedbackId: entityIdSchema,
    scope: resourceScopeSchema,
    reporter: actorSnapshotSchema.nullable(),
    report: feedbackReportSchema,
    status: feedbackStatusSchema,
    duplicateGroupId: entityIdSchema.nullable(),
    linkedBusinessImprovementId: entityIdSchema.nullable(),
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((feedback, context) => {
    if (feedback.status === 'merged-duplicate' && feedback.duplicateGroupId === null) {
      context.addIssue({
        code: 'custom',
        path: ['duplicateGroupId'],
        message: 'Merged feedback requires a duplicate group',
      });
    }
  });

export const selfHealProtectedAreaSchema = z.enum([
  'authorization-security',
  'production-data',
  'legal-form-or-decision',
  'approval-workflow',
  'mass-notification',
  'data-migration',
]);

export const selfHealChangeKindSchema = z.enum([
  'copy',
  'visual-style',
  'compatibility-bug',
  'performance-bug',
  'functional-bug',
  'feature',
  'data-change',
]);

export const selfHealRiskLevelSchema = z.enum(['low', 'medium', 'high', 'prohibited']);
export const feedbackTriageSchema = z
  .object({
    severity: z.enum(['low', 'medium', 'high', 'critical']),
    securityClass: z.enum(['none', 'suspected-secret', 'personal-data', 'security-incident']),
    legalImpact: z.enum(['none', 'review-required']),
    duplicateKey: entityIdSchema,
    humanGateRequired: z.boolean(),
    reasons: z.array(nonEmptyTextSchema.max(300)),
  })
  .strict()
  .superRefine((triage, context) => {
    if (
      (triage.severity === 'high' ||
        triage.severity === 'critical' ||
        triage.securityClass !== 'none' ||
        triage.legalImpact === 'review-required') &&
      !triage.humanGateRequired
    ) {
      context.addIssue({
        code: 'custom',
        path: ['humanGateRequired'],
        message: 'High-risk, security, and legal feedback requires a human gate',
      });
    }
  });

export const feedbackIssuePacketSchema = z
  .object({
    packetId: entityIdSchema,
    feedbackId: entityIdSchema,
    scope: resourceScopeSchema,
    title: nonEmptyTextSchema.max(200),
    sanitizedDescription: nonEmptyTextSchema.max(2_000),
    reproductionSteps: z.array(nonEmptyTextSchema.max(500)).min(1).max(10),
    expectedBehavior: nonEmptyTextSchema.max(500),
    observedBehavior: nonEmptyTextSchema.max(500),
    testSkeleton: nonEmptyTextSchema.max(4_000),
    externalIssueRef: z.null(),
    productionChangeAllowed: z.literal(false),
    humanGateRequired: z.boolean(),
    generatedAt: utcDateTimeSchema,
  })
  .strict();
export const verificationGateNameSchema = z.enum([
  'reproduction',
  'unit',
  'integration',
  'e2e',
  'regression',
  'security',
  'performance',
  'accessibility',
  'preview',
]);

export const verificationGateSchema = z
  .object({
    gate: verificationGateNameSchema,
    status: z.enum(['not-run', 'passed', 'failed', 'not-applicable']),
    evidenceRef: z.string().trim().min(1).max(500).nullable(),
  })
  .strict()
  .superRefine((gate, context) => {
    if (gate.status === 'passed' && gate.evidenceRef === null) {
      context.addIssue({
        code: 'custom',
        path: ['evidenceRef'],
        message: 'A passed verification gate requires evidence',
      });
    }
  });

const lowRiskChangeKinds: ReadonlySet<z.infer<typeof selfHealChangeKindSchema>> = new Set([
  'copy',
  'visual-style',
  'compatibility-bug',
  'performance-bug',
]);

export const selfHealAssessmentSchema = z
  .object({
    assessmentId: entityIdSchema,
    feedbackId: entityIdSchema,
    changeKind: selfHealChangeKindSchema,
    riskLevel: selfHealRiskLevelSchema,
    confidence: z.number().min(0).max(1),
    rationale: nonEmptyTextSchema.max(4_000),
    protectedAreas: z.array(selfHealProtectedAreaSchema),
    reproductionTestRef: z.string().trim().min(1).max(500).nullable(),
    isolatedBranchRef: z.string().trim().min(1).max(500).nullable(),
    previewRef: z.string().trim().min(1).max(500).nullable(),
    verification: z.array(verificationGateSchema),
    automaticPromotionRequested: z.boolean(),
    assessedAt: utcDateTimeSchema,
    assessedBy: z.enum(['ai', 'human']),
  })
  .strict()
  .superRefine((assessment, context) => {
    if (assessment.protectedAreas.length > 0 && assessment.riskLevel !== 'prohibited') {
      context.addIssue({
        code: 'custom',
        path: ['riskLevel'],
        message: 'Protected areas must be classified as prohibited for automatic change',
      });
    }
    if (assessment.riskLevel === 'low' && !lowRiskChangeKinds.has(assessment.changeKind)) {
      context.addIssue({
        code: 'custom',
        path: ['riskLevel'],
        message: 'This change kind cannot be classified as low risk',
      });
    }
    if (assessment.riskLevel === 'low' && assessment.confidence < 0.9) {
      context.addIssue({
        code: 'custom',
        path: ['confidence'],
        message: 'Low-risk automation requires classification confidence of at least 0.9',
      });
    }
    if (assessment.automaticPromotionRequested && assessment.riskLevel !== 'low') {
      context.addIssue({
        code: 'custom',
        path: ['automaticPromotionRequested'],
        message: 'Only low-risk changes may request automatic promotion',
      });
    }

    const gateNames = assessment.verification.map((gate) => gate.gate);
    if (new Set(gateNames).size !== gateNames.length) {
      context.addIssue({
        code: 'custom',
        path: ['verification'],
        message: 'Verification gates must be unique',
      });
    }
  });

export type FeedbackCategory = z.infer<typeof feedbackCategorySchema>;
export type FeedbackRecord = z.infer<typeof feedbackRecordSchema>;
export type FeedbackReport = z.infer<typeof feedbackReportSchema>;
export type SelfHealAssessment = z.infer<typeof selfHealAssessmentSchema>;
export type SelfHealRiskLevel = z.infer<typeof selfHealRiskLevelSchema>;
export type FeedbackTriage = z.infer<typeof feedbackTriageSchema>;
export type FeedbackIssuePacket = z.infer<typeof feedbackIssuePacketSchema>;

const mandatoryAutoPromotionGates = verificationGateNameSchema.options;

export function isSelfHealAutoPromotionEligible(assessment: SelfHealAssessment): boolean {
  if (
    assessment.riskLevel !== 'low' ||
    assessment.confidence < 0.9 ||
    assessment.protectedAreas.length > 0 ||
    !lowRiskChangeKinds.has(assessment.changeKind) ||
    assessment.reproductionTestRef === null ||
    assessment.isolatedBranchRef === null ||
    assessment.previewRef === null
  ) {
    return false;
  }

  const results = new Map(assessment.verification.map((gate) => [gate.gate, gate.status]));
  return mandatoryAutoPromotionGates.every((gate) => results.get(gate) === 'passed');
}
